import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import {
  ChevronLeft,
  Download,
  FileSpreadsheet,
  Loader2,
  RefreshCw,
  Send,
  AlertCircle,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import FileDropzone from "@/components/FileDropzone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  submitMutabakat,
  resetMutabakat,
} from "@/store/slices/mutabakatSlice";
import { downloadFromBase64 } from "@/services/mutabakatService";

const MutabakatPage = () => {
  const dispatch = useDispatch();
  const { loading, result, error } = useSelector((state) => state.mutabakat);

  const [zipFileName, setZipFileName] = useState("");
  const [files, setFiles] = useState([]);

  const canSubmit = zipFileName.trim().length > 0 && files.length > 0 && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const action = await dispatch(
      submitMutabakat({ zipFileName: zipFileName.trim(), files })
    );

    if (submitMutabakat.fulfilled.match(action)) {
      toast.success("Mutabakat formu başarıyla oluşturuldu.");
    } else if (submitMutabakat.rejected.match(action)) {
      toast.error(action.payload || "İşlem başarısız oldu.");
    }
  };

  const handleDownload = () => {
    if (!result?.bytes) return;
    try {
      downloadFromBase64(
        result.bytes,
        result.fileName || `${zipFileName || "mutabakat"}.zip`,
        result.contentType || "application/zip"
      );
      toast.success("Dosya indiriliyor.");
    } catch (err) {
      toast.error("Dosya indirilemedi.");
    }
  };

  const handleReset = () => {
    setZipFileName("");
    setFiles([]);
    dispatch(resetMutabakat());
  };

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return "-";
    try {
      return new Intl.NumberFormat("tr-TR", {
        style: "currency",
        currency: "TRY",
        minimumFractionDigits: 2,
      }).format(Number(amount));
    } catch {
      return String(amount);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-blue-600 transition-colors"
            data-testid="back-to-dashboard"
          >
            <ChevronLeft className="w-4 h-4" />
            Firma Araçları
          </Link>
        </div>

        {/* Header */}
        <div className="flex items-start gap-4 mb-8 md:mb-10">
          <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
            <FileSpreadsheet className="w-6 h-6" />
          </div>
          <div>
            <h1
              className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900"
              data-testid="page-title"
            >
              Mutabakat Form Oluşturucu
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              PDF dosyalarınızı toplu olarak yükleyin, ZIP dosya adını girin ve
              oluşturulan dosyayı indirin.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Form */}
          <Card className="lg:col-span-3 border-slate-200">
            <CardContent className="p-6 md:p-8">
              <form
                onSubmit={handleSubmit}
                className="space-y-6"
                data-testid="mutabakat-form"
              >
                <div className="space-y-2">
                  <Label
                    htmlFor="zipFileName"
                    className="text-xs font-semibold uppercase tracking-widest text-slate-600"
                  >
                    ZIP Dosya Adı
                  </Label>
                  <Input
                    id="zipFileName"
                    type="text"
                    placeholder="ornek-mutabakat-haziran"
                    value={zipFileName}
                    onChange={(e) => setZipFileName(e.target.value)}
                    disabled={loading}
                    className="h-11"
                    data-testid="zip-filename-input"
                  />
                  <p className="text-xs text-slate-400">
                    Oluşturulacak ZIP dosyasının adı (uzantı otomatik eklenir).
                  </p>
                </div>

                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-slate-600">
                    PDF Dosyaları
                  </Label>
                  <FileDropzone
                    files={files}
                    onChange={setFiles}
                    disabled={loading}
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button
                    type="submit"
                    disabled={!canSubmit}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-medium h-11 px-6"
                    data-testid="submit-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Gönderiliyor...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Formu Gönder
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleReset}
                    disabled={loading}
                    className="h-11"
                    data-testid="reset-button"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Temizle
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Sidebar - bilgi paneli */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="border-slate-200 bg-slate-900 text-white">
              <CardContent className="p-6">
                <div className="text-xs font-semibold uppercase tracking-widest text-blue-300 mb-3">
                  Nasıl Çalışır?
                </div>
                <ol className="space-y-3 text-sm text-slate-200">
                  <li className="flex gap-3">
                    <span className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold shrink-0">
                      1
                    </span>
                    <span>ZIP dosyanız için bir ad belirleyin.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold shrink-0">
                      2
                    </span>
                    <span>Birden fazla PDF dosyasını yükleyin.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold shrink-0">
                      3
                    </span>
                    <span>Formu gönderin ve sonuçları görüntüleyin.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold shrink-0">
                      4
                    </span>
                    <span>Oluşturulan ZIP dosyasını indirin.</span>
                  </li>
                </ol>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-2">
                  Form Özeti
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">ZIP Adı</span>
                    <span
                      className="text-slate-900 font-medium truncate ml-2"
                      data-testid="summary-zip-name"
                    >
                      {zipFileName || "—"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Dosya Sayısı</span>
                    <span
                      className="text-slate-900 font-medium"
                      data-testid="summary-file-count"
                    >
                      {files.length}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Hata mesajı */}
        {error && !loading && (
          <Alert
            variant="destructive"
            className="mt-8"
            data-testid="mutabakat-error"
          >
            <AlertCircle className="w-4 h-4" />
            <AlertTitle>İşlem başarısız</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Sonuçlar */}
        {result && (
          <section className="mt-10 space-y-6" data-testid="results-section">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
              <div>
                <h2 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">
                  Sonuçlar
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  İşlem tamamlandı. Aşağıda çıkarılan veriler ve indirilebilir dosya
                  bulunmaktadır.
                </p>
              </div>
              {result.bytes && (
                <Button
                  type="button"
                  onClick={handleDownload}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium h-11"
                  data-testid="download-zip-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {result.fileName || "Dosyayı"} İndir
                </Button>
              )}
            </div>

            <Card className="border-slate-200 overflow-hidden">
              <CardContent className="p-0">
                <Table data-testid="results-table">
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                        #
                      </TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                        Firma Adı
                      </TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                        Tarih
                      </TableHead>
                      <TableHead className="text-xs uppercase tracking-widest text-slate-500 font-semibold text-right">
                        Tutar
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.items && result.items.length > 0 ? (
                      result.items.map((item, idx) => (
                        <TableRow
                          key={idx}
                          data-testid={`result-row-${idx}`}
                          className="hover:bg-slate-50"
                        >
                          <TableCell className="text-slate-500 text-sm">
                            {idx + 1}
                          </TableCell>
                          <TableCell className="font-medium text-slate-900">
                            {item.companyName || "—"}
                          </TableCell>
                          <TableCell className="text-slate-700">
                            {item.date || "—"}
                          </TableCell>
                          <TableCell className="text-right font-mono text-slate-900">
                            {formatAmount(item.amount)}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="text-center text-slate-500 py-8"
                        >
                          Listelenecek kayıt bulunamadı.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </section>
        )}
      </main>
    </div>
  );
};

export default MutabakatPage;
